import React from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import {
    ArrowRight,
    Sparkles,
    MessageSquare,
    LayoutGrid,
    BrainCircuit,
    ShieldCheck,
    Zap,
    Users
} from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';

const FeatureCard = ({ icon: Icon, title, description, delay }) => (
    <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay }}
        className="p-8 rounded-[2.5rem] bg-white dark:bg-zinc-900/50 border border-neutral-200 dark:border-white/5 hover:border-blue-500/30 hover:shadow-lg transition-all duration-500 group shadow-sm dark:shadow-none"
    >
        <div className="w-14 h-14 rounded-2xl bg-blue-500/10 flex items-center justify-center mb-6 border border-blue-500/20 group-hover:scale-110 transition-transform">
            <Icon className="w-7 h-7 text-blue-600 dark:text-blue-400" />
        </div>
        <h3 className="text-2xl font-bold text-zinc-900 dark:text-white mb-4 tracking-tight">{title}</h3>
        <p className="text-neutral-500 dark:text-zinc-500 font-medium leading-relaxed">
            {description}
        </p>
    </motion.div>
);

const BenchrexLandingPage = () => {
    const navigate = useNavigate();

    return (
        <div className="min-h-screen bg-zinc-50 dark:bg-[#0A0A0A] text-zinc-900 dark:text-white selection:bg-blue-500/30 transition-colors duration-500">
            <Navbar />

            <main>
                {/* Hero Section */}
                <section className="relative pt-40 pb-32 px-6 overflow-hidden">
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-blue-600/10 blur-[120px] rounded-full pointer-events-none"></div>

                    <div className="max-w-7xl mx-auto text-center relative z-10">


                        <motion.h1
                            initial={{ opacity: 0, y: 30 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.1 }}
                            className="text-6xl md:text-8xl font-black tracking-tighter mb-8 leading-[0.9] text-zinc-900 dark:text-white"
                        >
                            Benchrex Portal: AI <br />
                            <span className="bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-400 dark:from-blue-400 dark:via-indigo-400 dark:to-blue-400 bg-clip-text text-transparent animate-gradient">
                                Powered Excellence.
                            </span>
                        </motion.h1>

                        <motion.p
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.2 }}
                            className="text-neutral-600 dark:text-zinc-400 text-xl md:text-2xl max-w-3xl mx-auto mb-12 font-medium leading-relaxed"
                        >
                            The ultimate student companion. Real-time doubt solving, personalized AI tutors, and seamless curriculum management in one beautiful interface.
                        </motion.p>

                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.8, delay: 0.3 }}
                            className="flex flex-wrap justify-center gap-6"
                        >
                            <button
                                onClick={() => navigate('https://benchrex.threnlabs.com')}
                                className="px-10 py-5 rounded-2xl bg-zinc-900 dark:bg-white text-white dark:text-black font-black text-xl hover:shadow-[0_0_40px_rgba(37,99,235,0.4)] transition-all active:scale-95 flex items-center gap-3"
                            >
                                Open Portal
                                <ArrowRight className="w-6 h-6" />
                            </button>
                            <button
                                onClick={() => document.getElementById('scholarsanchor-demo')?.scrollIntoView({ behavior: 'smooth' })}
                                className="px-10 py-5 rounded-2xl bg-white dark:bg-zinc-900 border border-neutral-200 dark:border-white/10 text-zinc-900 dark:text-white font-black text-xl hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-all active:scale-95 shadow-sm dark:shadow-none"
                            >
                                View Demo
                            </button>
                        </motion.div>

                        <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ duration: 1, delay: 0.4 }}
                            className="mt-24 relative"
                        >
                            <div className="absolute -inset-4 bg-gradient-to-b from-blue-600/20 to-transparent blur-3xl opacity-50 rounded-[3rem]"></div>
                            <img
                                src="/Benchrex1.png"
                                alt="Benchrex Portal Dashboard"
                                className="relative rounded-[2.5rem] border border-neutral-200 dark:border-white/10 shadow-2xl w-full max-w-5xl mx-auto"
                            />
                        </motion.div>
                    </div>
                </section>

                {/* Features Section */}
                <section className="py-20 px-6 bg-zinc-100 dark:bg-zinc-950/50 transition-colors duration-500">
                    <div className="max-w-7xl mx-auto">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                            <FeatureCard
                                icon={BrainCircuit}
                                title="AI Doubt Solver"
                                description="Instant, context-aware answers to academic questions based on your specific curriculum and notes."
                                delay={0.1}
                            />
                            <FeatureCard
                                icon={Users}
                                title="Expert Sessions"
                                description="Request human-in-the-loop support when you need deep dives or complex problem solving."
                                delay={0.2}
                            />
                            <FeatureCard
                                icon={LayoutGrid}
                                title="Notes Board"
                                description="A visual playground to organize your thoughts, pins, and important concepts from conversations."
                                delay={0.3}
                            />
                        </div>
                    </div>
                </section>

                {/* Visual Showcase 1 */}
                <section className="py-20 px-6 overflow-hidden">
                    <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center gap-20">
                        <div className="flex-1">
                            <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-500/10 border border-blue-500/20 rounded-full mb-6">
                                <Zap className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                                <span className="text-[10px] font-bold text-blue-600 dark:text-blue-400 uppercase tracking-widest">Real-time Intelligence</span>
                            </div>
                            <h2 className="text-4xl md:text-6xl font-black tracking-tighter mb-8 text-zinc-900 dark:text-white">
                                Conversation <br /> Context Matters.
                            </h2>
                            <p className="text-neutral-500 dark:text-zinc-500 text-lg md:text-xl font-medium leading-relaxed mb-10">
                                ScholarsAnchor doesn't just chat. It understands your academic journey, references your past notes, and provides sources for every claim.
                            </p>
                            <ul className="space-y-4">
                                {[
                                    "Personalized AI Personalities",
                                    "Source Citation & Validation",
                                    "Web Search Integration",
                                    "Topic-based Categorization"
                                ].map((item, i) => (
                                    <li key={i} className="flex items-center gap-3 text-zinc-900 dark:text-white font-bold">
                                        <div className="w-6 h-6 rounded-full bg-blue-500/20 flex items-center justify-center border border-blue-500/30">
                                            <div className="w-2 h-2 rounded-full bg-blue-600 dark:bg-blue-400" />
                                        </div>
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="flex-1 relative">
                            <div className="absolute inset-0 bg-blue-500/20 blur-[100px] rounded-full opacity-30"></div>
                            <motion.img
                                initial={{ opacity: 0, x: 50 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.8 }}
                                src="/Benchrex2.png"
                                alt="Conversations & Calendar View"
                                className="relative rounded-3xl border border-neutral-200 dark:border-white/10 shadow-2xl hover:scale-[1.02] transition-transform duration-500 w-full"
                            />
                        </div>
                    </div>
                </section>

                {/* Visual Showcase 2 */}
                <section className="py-20 px-6 bg-zinc-100/50 dark:bg-zinc-950/30 transition-colors duration-500">
                    <div className="max-w-7xl mx-auto flex flex-col lg:flex-row-reverse items-center gap-20">
                        <div className="flex-1">
                            <div className="inline-flex items-center gap-2 px-3 py-1 bg-indigo-500/10 border border-indigo-500/20 rounded-full mb-6">
                                <ShieldCheck className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
                                <span className="text-[10px] font-bold text-indigo-600 dark:text-indigo-400 uppercase tracking-widest">Enterprise Grade</span>
                            </div>
                            <h2 className="text-4xl md:text-6xl font-black tracking-tighter mb-8 text-zinc-900 dark:text-white">
                                Secure & Unified <br /> Experience.
                            </h2>
                            <p className="text-neutral-500 dark:text-zinc-500 text-lg md:text-xl font-medium leading-relaxed mb-10">
                                Built for institutions that demand the best. ScholarsAnchor enforces single-session security and integrates directly with your existing academic records.
                            </p>
                            <div className="grid grid-cols-2 gap-6">
                                <div className="p-6 rounded-2xl bg-white dark:bg-zinc-900/50 border border-neutral-200 dark:border-white/5 shadow-sm dark:shadow-none">
                                    <div className="text-zinc-900 dark:text-white font-black text-2xl mb-2">100%</div>
                                    <div className="text-neutral-500 dark:text-zinc-500 text-xs font-bold uppercase tracking-widest">Data Privacy</div>
                                </div>
                                <div className="p-6 rounded-2xl bg-white dark:bg-zinc-900/50 border border-neutral-200 dark:border-white/5 shadow-sm dark:shadow-none">
                                    <div className="text-zinc-900 dark:text-white font-black text-2xl mb-2">Instant</div>
                                    <div className="text-neutral-500 dark:text-zinc-500 text-xs font-bold uppercase tracking-widest">Setup Sync</div>
                                </div>
                            </div>
                        </div>
                        <div className="flex-1 relative">
                            <div className="absolute inset-0 bg-indigo-500/20 blur-[100px] rounded-full opacity-30"></div>
                            <motion.img
                                initial={{ opacity: 0, x: -50 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.8 }}
                                src="/benchrex3.png"
                                alt="Security & Settings View"
                                className="relative rounded-3xl border border-neutral-200 dark:border-white/10 shadow-2xl hover:scale-[1.02] transition-transform duration-500 w-full"
                            />
                        </div>
                    </div>
                </section>

                {/* Visual Showcase 3 */}
                <section className="py-20 px-6 bg-zinc-100/20 dark:bg-zinc-950/10 transition-colors duration-500">
                    <div className="max-w-7xl mx-auto flex flex-col lg:flex-row items-center gap-20">
                        <div className="flex-1">
                            <div className="inline-flex items-center gap-2 px-3 py-1 bg-purple-500/10 border border-purple-500/20 rounded-full mb-6">
                                <Sparkles className="w-4 h-4 text-purple-600 dark:text-purple-400" />
                                <span className="text-[10px] font-bold text-purple-600 dark:text-purple-400 uppercase tracking-widest">Next-Gen Learning</span>
                            </div>
                            <h2 className="text-4xl md:text-6xl font-black tracking-tighter mb-8 text-zinc-900 dark:text-white">
                                Deep AI <br /> Doubt Solver.
                            </h2>
                            <p className="text-neutral-500 dark:text-zinc-500 text-lg md:text-xl font-medium leading-relaxed mb-10">
                                Harness institutional intelligence. Upload your syllabus or notes, ask complex doubts, and receive instant, source-backed answers structured specifically for your courses.
                            </p>
                            <ul className="space-y-4">
                                {[
                                    "Curriculum-aligned Explanations",
                                    "Dynamic Homework Assistance",
                                    "Smart Concept Mapping",
                                    "Interactive AI Tutors"
                                ].map((item, i) => (
                                    <li key={i} className="flex items-center gap-3 text-zinc-900 dark:text-white font-bold">
                                        <div className="w-6 h-6 rounded-full bg-purple-500/20 flex items-center justify-center border border-purple-500/30">
                                            <div className="w-2 h-2 rounded-full bg-purple-600 dark:bg-purple-400" />
                                        </div>
                                        {item}
                                    </li>
                                ))}
                            </ul>
                        </div>
                        <div className="flex-1 relative">
                            <div className="absolute inset-0 bg-purple-500/20 blur-[100px] rounded-full opacity-30"></div>
                            <motion.img
                                initial={{ opacity: 0, x: 50 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.8 }}
                                src="/AI_Doubt_Solver.png"
                                alt="AI Doubt Solver View"
                                className="relative rounded-3xl border border-neutral-200 dark:border-white/10 shadow-2xl hover:scale-[1.02] transition-transform duration-500 w-full"
                            />
                        </div>
                    </div>
                </section>

                {/* Demo Video Section */}
                <section id="scholarsanchor-demo" className="pt-20 pb-0 relative overflow-hidden bg-zinc-100/40 dark:bg-zinc-950/20 transition-colors duration-500">
                    <div className="max-w-6xl mx-auto px-6">
                        <motion.div
                            initial={{ opacity: 0, y: 40 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 1 }}
                            className="relative"
                        >
                            <div className="absolute -inset-20 bg-blue-600/10 blur-[120px] rounded-full pointer-events-none"></div>
                            <div className="relative rounded-[3rem] overflow-hidden border-2 border-neutral-200 dark:border-white/10 shadow-[0_0_80px_rgba(0,0,0,0.1)] dark:shadow-[0_0_80px_rgba(0,0,0,0.8)] bg-black aspect-video">
                                <iframe
                                    className="w-full h-full"
                                    src="https://www.youtube.com/embed/xwhhdcbjbj?rel=0&modestbranding=1"
                                    title="ScholarsAnchor Product Demo"
                                    loading="lazy"
                                    allow="autoplay; encrypted-media; picture-in-picture"
                                    allowFullScreen
                                ></iframe>
                            </div>

                            <div className="absolute -top-6 left-1/2 -translate-x-1/2 px-8 py-3 rounded-full bg-zinc-900 dark:bg-white text-white dark:text-black font-black text-xs uppercase tracking-[0.3em] shadow-2xl z-20 whitespace-nowrap">
                                Benchrex Portal Walkthrough
                            </div>
                        </motion.div>
                    </div>
                </section>
            </main>

            <Footer />
        </div>
    );
};

export default BenchrexLandingPage;
